import React from "react";
import { Hero, Row } from "./AccountStatusPrompt";
import { GraceBannerView } from "./GraceBanner";
import PaymentSuccess from "../payments/PaymentSuccess";

/**
 * Development-only gallery of every account notice, with sample data, so the
 * wording and look can be reviewed without staging real clinic states.
 * Reachable at /alerts-preview while running `npm start`.
 */

const plus = (days) => new Date(Date.now() + days * 86400000);
const soon = plus(3);

const GROUPS = [
  {
    title: "Subscription renewal (paid clinics)",
    items: [30, 14, 7, 3, 2, 1, 0].map((d) => ({
      label: `${d} day${d === 1 ? "" : "s"} before the end date`,
      alert: {
        kind: "subscription",
        state: "due",
        urgent: d <= 7,
        days: d,
        date: plus(d),
        action: "renew",
      },
    })),
  },
  {
    title: "Grace period (day after the end date)",
    items: [
      {
        label: "Grace day: full access, banner + repeated popups",
        alert: {
          kind: "subscription",
          state: "grace",
          urgent: true,
          days: 0,
          date: plus(-1),
          pauseOn: plus(0),
          action: "renew",
        },
      },
    ],
  },
  {
    title: "Free trial",
    items: [
      {
        label: "Trial: the day before it ends",
        alert: { kind: "trial", state: "ending", urgent: true, days: 1, date: plus(1), action: "renew" },
      },
      {
        label: "Trial: last day",
        alert: { kind: "trial", state: "ending", urgent: true, days: 0, date: plus(0), action: "renew" },
      },
    ],
  },
  {
    title: "Credits — WhatsApp (Pro)",
    items: [
      { label: "Below 500", alert: { kind: "credits", state: "low", channel: "whatsapp", label: "WhatsApp", tier: 500, balance: 430, action: "recharge" } },
      { label: "Below 100", alert: { kind: "credits", state: "low", channel: "whatsapp", label: "WhatsApp", tier: 100, balance: 82, action: "recharge" } },
      { label: "Below 50", alert: { kind: "credits", state: "low", channel: "whatsapp", label: "WhatsApp", tier: 50, balance: 37, action: "recharge" } },
      { label: "Over (0) — shown daily for 3 days, then once", alert: { kind: "credits", state: "over", urgent: true, channel: "whatsapp", label: "WhatsApp", balance: 0, action: "recharge" } },
    ],
  },
  {
    title: "Credits — SMS (Essentials: 500 / 100 / 50, Pro: 200 / 100 / 50)",
    items: [
      { label: "Essentials: below 500", alert: { kind: "credits", state: "low", channel: "sms", label: "SMS", tier: 500, balance: 460, action: "recharge" } },
      { label: "Pro: below 200", alert: { kind: "credits", state: "low", channel: "sms", label: "SMS", tier: 200, balance: 180, action: "recharge" } },
      { label: "Below 100", alert: { kind: "credits", state: "low", channel: "sms", label: "SMS", tier: 100, balance: 91, action: "recharge" } },
      { label: "Below 50", alert: { kind: "credits", state: "low", channel: "sms", label: "SMS", tier: 50, balance: 24, action: "recharge" } },
      { label: "Over (0)", alert: { kind: "credits", state: "over", urgent: true, channel: "sms", label: "SMS", balance: 0, action: "recharge" } },
    ],
  },
];

const Frame = ({ label, children }) => (
  <div className="mb-8">
    <p className="mb-2 text-[13px] font-semibold text-darkgrey opacity-70">
      {label}
    </p>
    <div
      className="w-full max-w-[640px] overflow-hidden rounded-3xl bg-white"
      style={{ boxShadow: "0 24px 60px rgba(47,58,69,0.18)" }}
    >
      {children}
    </div>
  </div>
);

const noop = () => {};

const AlertPreview = () => {
  if (process.env.NODE_ENV === "production") return null;
  return (
    <div className="min-h-screen px-4 py-8 sm:px-10" style={{ background: "#EEF2F6" }}>
      <h1 className="mb-1 text-[22px] font-bold text-darkgrey">
        Account notices — preview
      </h1>
      <p className="mb-8 text-[14px] text-darkgrey opacity-70">
        Sample data only. Every notice a clinic can see after login.
      </p>

      {GROUPS.map((group) => (
        <section key={group.title} className="mb-10">
          <h2 className="mb-4 text-[16px] font-bold text-darkgrey">
            {group.title}
          </h2>
          {group.items.map((item) => (
            <Frame key={item.label} label={item.label}>
              <Hero alert={item.alert} onAction={noop} onDismiss={noop} hasMore={false} />
            </Frame>
          ))}
        </section>
      ))}

      <section className="mb-10">
        <h2 className="mb-4 text-[16px] font-bold text-darkgrey">
          Several notices together (main notice + rows)
        </h2>
        <Frame label="Subscription due in 7 days, WhatsApp over, SMS below 50">
          <Hero
            alert={GROUPS[0].items[2].alert}
            onAction={noop}
            onDismiss={noop}
            hasMore
          />
          <div className="px-6 pb-2 pt-3 sm:px-10">
            <div className="divide-y" style={{ borderColor: "#E6EBF0" }}>
              <Row alert={GROUPS[3].items[3].alert} onAction={noop} />
              <Row alert={GROUPS[4].items[3].alert} onAction={noop} />
            </div>
            <div className="flex justify-end py-3 text-[14px] font-medium" style={{ color: "#6B7683" }}>
              Remind me later
            </div>
          </div>
        </Frame>
      </section>

      <section className="mb-10">
        <h2 className="mb-4 text-[16px] font-bold text-darkgrey">
          After payment — success screens
        </h2>
        <Frame label="Credit purchase (WhatsApp + SMS)">
          <PaymentSuccess
            onDone={noop}
            onMore={noop}
            order={{
              _id: "preview-credit",
              invoiceReady: true,
              kind: "credits",
              items: [
                { channel: "whatsapp", messages: 500, quantity: 1 },
                { channel: "sms", messages: 200, quantity: 1 },
              ],
              subTotal: 149,
              taxTotal: 26.82,
              taxPercentage: 18,
              roundOff: 0.18,
              total: 176,
              invoiceNumber: "INV-000221",
              payment: { mode: "upi", reference: "628719145999", paidAt: new Date() },
              change: {
                before: { whatsapp: 120, sms: 80 },
                after: { whatsapp: 620, sms: 280 },
              },
            }}
          />
        </Frame>
        <Frame label="Plan renewal">
          <PaymentSuccess
            onDone={noop}
            order={{
              _id: "preview-plan",
              invoiceReady: false,
              kind: "plan",
              plan: {
                name: "Pro Yearly",
                term: "Yearly",
                sms: 1000,
                whatsapp: 5000,
                startedAt: plus(4),
                expiresAt: plus(369),
              },
              subTotal: 9153,
              taxTotal: 1647.54,
              taxPercentage: 18,
              roundOff: 0.46,
              total: 10801,
              payment: { mode: "upi", reference: "628719145877", paidAt: new Date() },
            }}
          />
        </Frame>
      </section>

      <section className="mb-10">
        <h2 className="mb-4 text-[16px] font-bold text-darkgrey">
          Grace-period banner (top of every page)
        </h2>
        <div className="max-w-[900px] overflow-hidden rounded-xl">
          <GraceBannerView graceEndsOn={soon} canRenew onRenew={noop} onClose={noop} />
        </div>
      </section>
    </div>
  );
};

export default AlertPreview;
